import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { format, getYear } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { debounce, fromEvent, Subscription, timer } from 'rxjs';
import { NgxEchartsModule, NGX_ECHARTS_CONFIG } from 'ngx-echarts';
import * as echarts from 'echarts';
import { IMarketTrendsDataSet, ITrendsDataPoint } from '../../../../../models/IMarketTrendsGraphSet';
import { TrendDataTypeEnum } from '../../../../../models/ITrendsTableData';
import { MarketTrendsChartConfiguration, TrendsChartStyles } from '../../../../../models/MarketTrendsChartConfiguration';
import { ChartService } from '../../../../../services/chart.service';
import { MarketTrendsService } from '../../../../../services/market-trends.service';
import { ThemeService } from '../../../../../services/theme.service';
import { selectTrendsDataHeader } from '../../../../../store/selectors/market-trends.selector';
import { IAppState } from '../../../../../store/state/app.state';
import { SharedComponentsModule } from '../../../shared/shared-components.module';

@Component({
  standalone: true,
  imports: [SharedComponentsModule, CommonModule, NgxEchartsModule],
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss']
})
export class ChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() chart: MarketTrendsChartConfiguration;
  @Input() dataPoints: IMarketTrendsDataSet;
  @ViewChild('chartContainer', { static: false }) chartContainer: any;

  subs = new Subscription();
  chartId: string;
  chartData: ITrendsDataPoint[][];
  echartsOptions: any = {};
  palette: string[];
  TrendDataTypeEnum = TrendDataTypeEnum;
  showColumnMarkers = false;
  debouncedResize$ = fromEvent(window, 'resize');

  private year = 0;
  private currMonth: string;
  private chartInstance: echarts.ECharts;

  constructor(
    private marketTrendsService: MarketTrendsService,
    private store: Store<IAppState>,
    private decimalPipe: DecimalPipe,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private chartSvc: ChartService
  ) {
    this.chartId = MarketTrendsService.randomId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.chart) {
      this.generateChart();
    }
  }

  ngOnInit(): void {
    this.subs.add(
      this.debouncedResize$.pipe(debounce(() => timer(500))).subscribe(() => {
        this.generateChart();
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      })
    );

    this.generateChart();

    this.store.select(selectTrendsDataHeader).subscribe(d => {
      this.chartData = [];
      if (d) {
        this.currMonth = format(new Date(d.endDate), 'MMMM');
      }
      Object.values(this.dataPoints).map(value => {
        this.formatDates(value);
      });
      this.updateChartOptions();
    });
  }

  generateChart(): void {
    this.showColumnMarkers = window.innerWidth >= 960;
    this.fetchChartConfig(this.chart?.chartTypes, this.chart?.isCustomChart);
    this.setUpResponsiveDataMarkers();
    this.cdr.detectChanges();
  }

  private isMobile() {
    return window.innerWidth <= 600;
  }

  private updateChartOptions(): void {
    const config = this.fetchChartConfigForEcharts(this.chart?.chartTypes, this.chart?.isCustomChart);
    this.echartsOptions = { ...config };
    this.cdr.detectChanges();
  }

  onChartInit(ec: echarts.ECharts): void {
    this.chartInstance = ec;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  private checkColumn() {
    return this.showColumnMarkers && this.chart?.chartTypes.includes('Column');
  }

  private setUpResponsiveDataMarkers() {
    // Column markers functionality is handled directly in ECharts data labels
  }

  private formatDates(value: ITrendsDataPoint[]) {
    const val = cloneDeep(value);
    let year = 0;
    val.map(v => {
      const d = new Date(v.xData);
      const month = format(d, 'MMMM').substring(0, 3);
      year = getYear(d);
      v.xData = `${month} ${year}`;
    });
    this.chartData.push(val);
  }

  private fetchChartConfig(size: TrendsChartStyles[], customChart = false) {
    const config = this.chartSvc.fetchChartConfig(size, customChart);
    return Object.assign(this, config);
  }

  private fetchChartConfigForEcharts(size: TrendsChartStyles[], customChart = false) {
    return this.chartSvc.fetchEchartsChartConfig(
      size,
      customChart,
      this.chartData,
      this.chart,
      this.currMonth,
      this.isMobile(),
      this.showColumnMarkers,
      this.themeService,
      this.decimalPipe
    );
  }
}
